import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchAndFilterUserCustomizedDesign, getAllCustomerCustomizedDesign } from '../../redux/slices/designSlice';
import {
  addToProductOrProjectToFavorites,
  removeProductOrProjectFromFavorites,
  addProductToCart,
  deleteUserCustomizedDesign,
  removeProductFromCart,
} from '../../redux/slices/designSlice';

import { useNavigate } from 'react-router-dom';

/// SVG icons as components
const HeartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const HeartIconSolid = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
  </svg>
);

const ShoppingCartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// Shimmer Effect Component
const ShimmerEffect = ({ width, height, className }) => {
  return (
    <div
      className={`animate-pulse bg-gray-200 ${className}`}
      style={{ width, height }}
    ></div>
  );
};

export default function MyDesigns() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { categories } = useSelector((state) => state.home);
  const {
    getAllCustomerCustomizedDesignLoading,
    getAllCustomerCustomizedDesignError,
    getAllCustomerCustomizedDesignData,
  } = useSelector((state) => state.design);

  useEffect(() => {
    // Fetch all customer customized designs on component mount
    dispatch(getAllCustomerCustomizedDesign());
  }, [dispatch]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [dateRange, setDateRange] = useState('');
  const [filteredDesigns, setFilteredDesigns] = useState(null);
  const [viewStates, setViewStates] = useState({}); // Store toggle state for each design

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(4); // Show 4 items per page

  // Calculate pagination
  const currentItemsData = filteredDesigns || getAllCustomerCustomizedDesignData || [];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = currentItemsData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil((currentItemsData.length || 0) / itemsPerPage);
  // Initialize viewStates when data is fetched
  useEffect(() => {
    if (getAllCustomerCustomizedDesignData) {
      const initialViewStates = {};
      getAllCustomerCustomizedDesignData.forEach((design) => {
        initialViewStates[design.id] = 'front'; // Default to 'front' view
      });
      setViewStates(initialViewStates);
    }
  }, [getAllCustomerCustomizedDesignData]);

  // Dimensions for the preview pane
  const previewWidth = 400; // Width of the preview pane
  const previewHeight = 300; // Height of the preview pane

  // Handle filter submission
  const handleApplyFilters = () => {
    const filterParams = {
      search_term: searchTerm,
      categories: selectedCategories,
      sub_categories: selectedSubcategories,
      date_created: dateRange ? [dateRange] : [],
    };

    dispatch(searchAndFilterUserCustomizedDesign({ searchTerm, filterParams }))
      .unwrap()
      .then((response) => {
        setFilteredDesigns(response.data); // Assuming the response contains filtered data
      });
    setCurrentPage(1);
  };


  // Handle clearing filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setDateRange('');
    dispatch(searchAndFilterUserCustomizedDesign({ searchTerm: '', filterParams: {} }));
    setCurrentPage(1); // Reset to first page when filters are cleared
  };

  // Reconstruct layers for designs
  const reconstructLayers = (design, containerWidth, containerHeight) => {
    // The original design dimensions from the Design page (500x400)
    const originalDesignWidth = 500;
    const originalDesignHeight = 400;

    // Calculate scaling factors
    const scaleX = containerWidth / originalDesignWidth;
    const scaleY = containerHeight / originalDesignHeight;

    const processLayer = (layer) => {
      const baseLayer = {
        id: layer.id,
        type: layer.type,
        x: layer.x * scaleX,
        y: layer.y * scaleY,
        width: layer.width * scaleX,
        height: layer.height * scaleY,
        rotation: layer.rotation,
        flipped: layer.flipped,
        visible: layer.visible,
        zIndex: layer.z_index,
        borderColor: layer.border_color,
        borderStyle: layer.border_style,
        offsetX: layer.offset_x * scaleX,
        offsetY: layer.offset_y * scaleY,
      };

      if (layer.type === 'image' && layer.image_data) {
        return {
          ...baseLayer,
          src: layer.image_data.original_src || layer.image_data.src || '',
        };
      } else if (layer.type === 'text' && layer.text_data) {
        return {
          ...baseLayer,
          text: layer.text_data.text,
          color: layer.text_data.color,
        };
      }
      return baseLayer;
    };

    const frontLayers = design.layers.filter((l) => l.side === 'front').map(processLayer);
    const backLayers = design.layers.filter((l) => l.side === 'back').map(processLayer);

    return { frontLayers, backLayers };
  };

  // Toggle view for a specific design
  const toggleView = (designId) => {
    setViewStates((prev) => ({
      ...prev,
      [designId]: prev[designId] === 'front' ? 'back' : 'front',
    }));
  };

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle add to cart
  const handleAddToCart = (designId) => {
    // Implement add to cart logic
    const isInCart = getAllCustomerCustomizedDesignData.find((design) => design.id === designId).is_in_cart;
    if (isInCart) {
      dispatch(removeProductFromCart(designId));
    }
    else {
      dispatch(addProductToCart({item_id: designId, product_type: 'project'}));
    }
  };

  // Handle toggle favorite
  const handleToggleFavorite = (designId) => {
    // Implement toggle favorite logic
    const isFavorite = getAllCustomerCustomizedDesignData.find((design) => design.id === designId).is_favorite;
    if (isFavorite) {
      dispatch(removeProductOrProjectFromFavorites(designId));
    }
    else {
      dispatch(addToProductOrProjectToFavorites({item_id:designId, product_type:'project'}));
    }
  };

  // Handle edit design
  const handleEditDesign = (product_id, designId) => {
    // Implement edit design logic
    navigate(`/design/${product_id}/false/${designId}`);
  };

  // Handle delete design
  const handleDeleteDesign = (designId) => {
    dispatch(deleteUserCustomizedDesign(designId))
  };

  return (
    <div className="bg-[#EEE8E8] min-h-screen py-8">
      <div className="max-w-screen-xl mx-auto px-4">
        {/* Page Title */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-[#00148c]">My Designs</h1>

        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* LEFT SIDEBAR: Filters & Sorting */}
          <div className="w-full lg:w-64 bg-white p-6 rounded-lg shadow-sm h-fit sticky top-8">
            <h2 className="text-lg font-bold mb-6 text-[#00148c]">Filters & Sorting</h2>

            {/* Search Bar */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search designs..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00148c] focus:border-transparent"
              />
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Categories</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {categories.map((category) => (
                  <label key={category.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#00148c]"
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => {
                        setSelectedCategories((prev) =>
                          prev.includes(category.id)
                            ? prev.filter((id) => id !== category.id)
                            : [...prev, category.id]
                        );
                      }}
                    />
                    {category.name}
                  </label>
                ))}
              </div>
            </div>

            {/* Subcategories */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-2">Subcategories</h3>
              <div className="flex flex-col gap-1 text-sm max-h-32 overflow-y-auto">
                {categories
                  .filter((cat) => selectedCategories.includes(cat.id))
                  .flatMap((cat) => cat.subcategories)
                  .map((sub) => (
                    <label key={sub.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-[#00148c]"
                        checked={selectedSubcategories.includes(sub.id)}
                        onChange={() => {
                          setSelectedSubcategories((prev) =>
                            prev.includes(sub.id)
                              ? prev.filter((id) => id !== sub.id)
                              : [...prev, sub.id]
                          );
                        }}
                      />
                      {sub.name}
                    </label>
                  ))}
              </div>
            </div>

            {/* Date Created */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-2">Date Created</h3>
              <div className="flex flex-col gap-1 text-sm">
                {['Last 30 days', 'Last 90 days', 'Custom'].map((range) => (
                  <label key={range} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="date-range"
                      className="accent-[#00148c]"
                      checked={dateRange === range}
                      onChange={() => setDateRange(range)}
                    />
                    {range}
                  </label>
                ))}
              </div>
            </div>

            {/* Apply and Clear Filters */}
            <div className="flex flex-col gap-3 mt-8">
              <button
                onClick={handleApplyFilters}
                className="w-full bg-[#00148c] text-white font-medium py-2 px-4 rounded-lg hover:bg-[#002080] transition-colors"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="w-full bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: My Designs List */}
          <div className="flex-1">
            {/* Loading state - keep this the same */}

            {/* Empty state - keep this the same */}

            {/* Design items */}
            <div className="grid gap-6">
              {getAllCustomerCustomizedDesignLoading && (
                <div className="space-y-4">
                  {/* Shimmer for title */}
                  <ShimmerEffect width="50%" height="24px" className="rounded-md" />
                  {/* Shimmer for design cards */}
                  {[...Array(3)].map((_, index) => (
                    <div key={index} className="p-4 bg-white rounded-md shadow-sm">
                      <ShimmerEffect width="100%" height="200px" className="rounded-md mb-4" />
                      <ShimmerEffect width="60%" height="16px" className="rounded-md mb-2" />
                      <ShimmerEffect width="40%" height="16px" className="rounded-md" />
                    </div>
                  ))}
                </div>
              )}

              {!getAllCustomerCustomizedDesignLoading &&
                getAllCustomerCustomizedDesignData &&
                getAllCustomerCustomizedDesignData.length === 0 && (
                  <div className="text-center text-gray-600">
                    <p className="text-lg font-medium">No designs found.</p>
                    <p>Try adjusting your filters or creating a new design.</p>
                  </div>
                )}

              {getAllCustomerCustomizedDesignData &&
                currentItems.map((design) => {
                  const { product } = design;
                  const mediaFiles = product?.images || {};
                  const front_image = mediaFiles.front_image || '';
                  const back_image = mediaFiles.back_image || front_image;

                  const { frontLayers, backLayers } = reconstructLayers(
                    design,
                    previewWidth,
                    previewHeight
                  );

                  const layers = viewStates[design.id] === 'back' ? backLayers : frontLayers;
                  const imageSrc = viewStates[design.id] === 'back' ? back_image : front_image;

                  return (
                    <div key={design.id} className="bg-white p-6 rounded-lg shadow-sm">
                      <div className="flex flex-col md:flex-row gap-8">
                        {/* Preview Pane */}
                        <div className="w-full md:w-1/2">
                          <div
                            className="relative bg-gray-100 rounded-lg overflow-hidden"
                            style={{ width: '100%', height: previewHeight }}
                          >
                            <img
                              src={imageSrc}
                              alt={viewStates[design.id] === 'back' ? 'Back' : 'Front'}
                              className="absolute top-0 left-0 w-full h-full object-contain"
                            />
                            {layers.map((layer) => (
                              layer.visible && (
                                <div
                                  key={layer.id}
                                  className="absolute"
                                  style={{
                                    top: layer.y,
                                    left: layer.x,
                                    width: layer.width,
                                    height: layer.height,
                                    transform: `rotate(${layer.rotation}deg) scaleX(${layer.flipped ? -1 : 1})`,
                                    zIndex: layer.zIndex,
                                    borderTopWidth: layer.border?.top || 0,
                                    borderRightWidth: layer.border?.right || 0,
                                    borderBottomWidth: layer.border?.bottom || 0,
                                    borderLeftWidth: layer.border?.left || 0,
                                    borderColor: layer.borderColor,
                                    borderStyle: layer.borderStyle,
                                    boxShadow: `${layer.offsetX}px ${layer.offsetY}px 8px rgba(0,0,0,0.2)`,
                                  }}
                                >
                                  {layer.type === 'text' ? (
                                    <div
                                      className="w-full h-full flex items-center justify-center"
                                      style={{ color: layer.color }}
                                    >
                                      {layer.text}
                                    </div>
                                  ) : (
                                    <img
                                      src={layer.src}
                                      alt="Layer"
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                              )
                            ))}
                          </div>

                          {/* View Toggle */}
                          <div className="flex justify-center mt-4">
                            <div className="inline-flex rounded-full bg-gray-100 p-1 shadow-inner border border-gray-200">
                              <button
                                onClick={() => toggleView(design.id)}
                                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${viewStates[design.id] === 'front'
                                  ? 'bg-[#00148c] text-white shadow-md transform scale-105'
                                  : 'text-gray-600 hover:bg-gray-50'
                                  }`}
                              >
                                <span className="flex items-center justify-center gap-1">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M21 12H3M12 21V3M12 21L3 12M12 21L21 12M12 3L3 12M12 3L21 12" strokeWidth="2" strokeLinecap="round" />
                                  </svg>
                                  Front
                                </span>
                              </button>
                              <button
                                onClick={() => toggleView(design.id)}
                                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${viewStates[design.id] === 'back'
                                  ? 'bg-[#00148c] text-white shadow-md transform scale-105'
                                  : 'text-gray-600 hover:bg-gray-50'
                                  }`}
                              >
                                <span className="flex items-center justify-center gap-1">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M12 3H3V21H21V12M12 21V3M12 21L21 12M12 3L21 12" strokeWidth="2" strokeLinecap="round" />
                                  </svg>
                                  Back
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Product Details and Actions */}
                        <div className="w-full md:w-1/2 flex flex-col">
                          <h3 className="text-xl font-bold mb-4 text-gray-800">{design.name}</h3>

                          <div className="space-y-2 mb-6">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Product Name:</span> {product?.product_name || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Created:</span> {new Date(design.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Price:</span> ${product?.price || '0.00'}
                            </p>

                          </div>

                          {/* Action Buttons */}
                          <div className="mt-auto grid grid-cols-2 gap-3">
                            <button
                              onClick={() => handleAddToCart(design.id)}
                              className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg ${design.is_in_cart
                                ? 'bg-green-500 text-white'
                                : 'bg-[#00148c] text-white hover:bg-[#002080]'
                                } transition-colors`}
                            >
                              <ShoppingCartIcon className="h-5 w-5" />
                              {design.is_in_cart ? 'In Cart' : 'Add to Cart'}
                            </button>

                            <button
                              onClick={() => handleToggleFavorite(design.id)}
                              className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg ${design.is_favorite
                                ? 'bg-pink-100 text-pink-600'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                } transition-colors`}
                            >
                              {design.is_favorite ? (
                                <HeartIconSolid className="h-5 w-5 text-pink-600" />
                              ) : (
                                <HeartIcon className="h-5 w-5" />
                              )}
                              Favorite
                            </button>

                            <button
                              onClick={() => handleEditDesign(product.sub_category.id, design.id)}
                              className="flex items-center justify-center gap-2 py-2 px-4 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                            >
                              <PencilIcon className="h-5 w-5" />
                              Edit
                            </button>

                            <button
                              onClick={() => handleDeleteDesign(design.id)}
                              className="flex items-center justify-center gap-2 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                              <TrashIcon className="h-5 w-5" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Pagination Controls - keep this the same */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex items-center gap-1">
                  {/* First Page */}
                  <button
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md border text-sm ${currentPage === 1
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    «
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md border text-sm ${currentPage === 1
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    ‹
                  </button>

                  {/* Current Page and Nearby Pages */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`px-3 py-1 rounded-md border text-sm ${currentPage === pageNumber
                          ? 'bg-[#00148c] text-white border-[#00148c]'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}

                  {/* Ellipsis for many pages */}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <span className="px-2">...</span>
                  )}

                  {/* Last Page (if not shown) */}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <button
                      onClick={() => paginate(totalPages)}
                      className={`px-3 py-1 rounded-md border text-sm ${currentPage === totalPages
                        ? 'bg-[#00148c] text-white border-[#00148c]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {totalPages}
                    </button>
                  )}

                  {/* Next Page */}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md border text-sm ${currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    ›
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => paginate(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md border text-sm ${currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    »
                  </button>
                </nav>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}